import {
  Box,
  CircularProgress,
  Divider,
  Link,
  Popover,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import dayjs from "../../utils/dayjs";
import { usePerspective, useCustomer } from "../../contexts/app/provider";
import { usePerspectiveAuthRetry } from "../../services/perspective_api/use_auth_retry";
import { convertToClientFeatures } from "../../types/feature_flags";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, FullParsedParquetData } from "../../utils/parquet";
import { RunSettings } from "../../services/appsync/types";
import {
  closePoCellPopover,
  usePoCellPopoverTarget,
  PoCellPopoverTarget,
} from "./store";

interface PoCellDetailRow {
  purchase_order_id: string;
  sku: string;
  sku_name: string | null;
  location: string;
  supplier: string | null;
  quantity: number;
  unit_cost: number | null;
  order_placed_date: number | null;
  order_delivery_date: number | null;
  status: string;
  lead_time_days: number | null;
  /**
   * Container the bin-packer assigned this order to ("C01", or "C01, C02" when
   * split). Only the wave the packer ran over has one — later simulated waves
   * come back null and the field is omitted.
   */
  container_label: string | null;
  /** Uncapped PO count for the cell (rows are capped at 10 server-side). */
  total_po_count: number | null;
}

interface FetchEntry {
  loading: boolean;
  error: string | null;
  rows: PoCellDetailRow[] | null;
}

// Module-level cache so reopening a cell never refetches within a session.
// Keyed by run + cell identity; a new run settings object is a new key.
const detailCache = new Map<string, FetchEntry>();

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v); // arrow Int64 arrives as bigint
  return Number.isNaN(n) ? null : n;
};

function arrowToRows(parsed: FullParsedParquetData): PoCellDetailRow[] {
  return parsed.arrowTable.toArray().map((r: any) => ({
    purchase_order_id: String(r.purchase_order_id ?? ""),
    sku: String(r.sku ?? ""),
    sku_name: r.sku_name != null ? String(r.sku_name) : null,
    location: String(r.location ?? ""),
    supplier: r.supplier != null ? String(r.supplier) : null,
    quantity: toNumberOrNull(r.quantity) ?? 0,
    unit_cost: toNumberOrNull(r.unit_cost),
    order_placed_date: toNumberOrNull(r.order_placed_date),
    order_delivery_date: toNumberOrNull(r.order_delivery_date),
    status: String(r.status ?? ""),
    lead_time_days: toNumberOrNull(r.lead_time_days),
    container_label:
      r.container_label != null ? String(r.container_label) : null,
    total_po_count: toNumberOrNull(r.total_po_count),
  }));
}

const formatDate = (ms: number | null): string =>
  ms == null ? "—" : dayjs.utc(ms).format("MM/DD/YY");

const formatQty = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const FIELD_LABEL_SX = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: (t: any) => t.palette.atomic.muted,
};

const FIELD_VALUE_SX = {
  fontSize: "12px",
  fontVariantNumeric: "tabular-nums",
  color: (t: any) => t.palette.atomic.ink,
};

const FOOTER_LINK_SX = {
  fontSize: "12px",
  fontWeight: 600,
  color: (t: any) => t.palette.atomic.brandBlueDark,
};

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "16px",
        py: "3px",
      }}
    >
      <Box sx={FIELD_LABEL_SX}>{label}</Box>
      <Box sx={FIELD_VALUE_SX}>{value}</Box>
    </Box>
  );
}

/**
 * The Supply view (`purchase_order_master`) statuses that correspond to each
 * popover cell kind, so "View in Supply" lands on the same POs the popover
 * just listed.
 *
 * The two datasets label the same orders differently. `po_plan_edit` (what the
 * popover reads) marks undelivered ERP history "Open" and the next planned
 * order "Needs Review"; `purchase_order_master` marks the same undelivered
 * history "Ordered" and every planned order "Simulated" (see
 * logic_purchase_order_master — all four customer variants agree). Delivered
 * history is "Delivered" in both and belongs to neither kind: an arrival cell's
 * "open" breakdown is undelivered-on-order, which is "Ordered" here.
 */
const SUPPLY_STATUSES_BY_KIND: Record<PoCellPopoverTarget["kind"], string[]> = {
  open: ["Ordered"],
  simulated: ["Simulated"],
  all: ["Ordered", "Simulated"],
};

/**
 * Footer links out to the full views, pre-scoped to the clicked cell.
 *
 * Both routes read `filters` off the URL through use_date_pivot_params, which
 * REPLACES the view's default filter list rather than merging — so each link
 * re-states every filter that view needs, not just the SKU.
 */
function PopoverFooterLinks({ target }: { target: PoCellPopoverTarget }) {
  const { customerSettings } = useCustomer();
  const features = useMemo(
    () => convertToClientFeatures(customerSettings?.features),
    [customerSettings],
  );

  const toFilters = (filters: unknown[]) =>
    `?filters=${encodeURIComponent(JSON.stringify(filters))}`;

  return (
    <Box sx={{ mt: "10px", display: "flex", gap: "14px" }}>
      {/*
        Gated on the same flag as the nav entry (left_menu). The /po-inbox
        route itself carries no guard, so an unconditional link here would hand
        customers without the flag a way into a page deliberately kept out of
        their nav.
      */}
      {features?.POInbox && (
        <Link
          component={RouterLink}
          to={`/po-inbox${toFilters([
            // Preserve the default "Needs Review" tab (the status filter is
            // what drives the tabs) and add a SKU filter so the inbox lands
            // pre-scoped to the PO cell the user clicked.
            ["status", "in", ["Needs Review"]],
            ["sku", "in", [target.sku]],
          ])}`}
          onClick={closePoCellPopover}
          sx={FOOTER_LINK_SX}
        >
          View in PO Inbox
        </Link>
      )}
      <Link
        component={RouterLink}
        to={`/supply${toFilters([
          ["status", "in", SUPPLY_STATUSES_BY_KIND[target.kind]],
          ["sku", "in", [target.sku]],
        ])}`}
        onClick={closePoCellPopover}
        sx={FOOTER_LINK_SX}
      >
        View in Supply
      </Link>
    </Box>
  );
}

/**
 * Popover with the individual POs behind one open/simulated breakdown cell.
 *
 * Render-isolation contract: this component (and its module store) is the ONLY
 * thing that re-renders on open/close/data-arrival. It must never feed state
 * back into the matrix table — the clicked-cell outline is applied
 * imperatively to the anchor element for the same reason.
 */
export function PoCellPopover({
  runSettings,
  dateHierarchy,
}: {
  runSettings: RunSettings | null;
  dateHierarchy: "day" | "week" | "month";
}) {
  const target = usePoCellPopoverTarget();
  const { perspectiveApiService } = usePerspective();
  const theme = useTheme();
  const outlineColor = theme.palette.atomic.brandBlue;
  const [entry, setEntry] = useState<FetchEntry>({
    loading: false,
    error: null,
    rows: null,
  });
  // Stale socket token (SUNGLAS-39): one recovery per cell, then resubscribe.
  const { retryNonce, recoverFrom, markSucceeded } = usePerspectiveAuthRetry(
    perspectiveApiService,
  );

  // Outline the clicked cell while the popover is open (imperative on purpose
  // — see render-isolation contract above).
  useEffect(() => {
    if (!target) return;
    const el = target.anchorEl;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = `2px solid ${outlineColor}`;
    el.style.outlineOffset = "-2px";
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [target, outlineColor]);

  useEffect(() => {
    if (!target) return;
    // A late async continuation (parseParquetData can do a network fetch) must
    // not write into a popover that's since re-anchored to another cell.
    // unsubscribe() can't cancel an already-started handler, so gate every
    // setEntry on this effect-scoped flag. Cache writes are fine either way —
    // they're keyed by cell.
    let cancelled = false;
    const cacheKey = JSON.stringify([
      runSettings,
      target.sku,
      target.location,
      target.periodStartMs,
      target.kind,
      target.bucketBy,
      dateHierarchy,
    ]);
    // Only completed fetches count as cache hits. A stale {loading:true}
    // entry (popover closed mid-flight — the cleanup unsubscribes) or an
    // error entry must refetch, or reopening would spin forever.
    const cached = detailCache.get(cacheKey);
    if (cached?.rows) {
      setEntry(cached);
      return;
    }
    const loadingEntry: FetchEntry = { loading: true, error: null, rows: null };
    detailCache.set(cacheKey, loadingEntry);
    setEntry(loadingEntry);

    // Failure is latched here and resolved on `complete` — see the handlers.
    let pendingError: string | null = null;
    let sawData = false;

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({
        query: GetTransformedRawParquetDocument,
        variables: {
          datasetName: "po_plan_edit",
          transformId: "po_cell_details",
          folder: "outputs",
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: null,
          format: "parquet" as const,
          params: {
            sku: target.sku,
            // Omit location for SKU-level cells so the transform matches on
            // sku alone (across all locations).
            ...(target.location != null ? { location: target.location } : {}),
            period_start: new Date(target.periodStartMs).toISOString(),
            date_hierarchy: dateHierarchy,
            kind: target.kind,
            bucket_by: target.bucketBy,
          },
        },
      })
      .subscribe({
        next: async (res: any) => {
          const payload = res?.data?.getTransformedRawParquet;
          const parquetData = payload?.parquetData;
          // Latch a failure rather than rendering it straight away. The
          // server's FIRST emit reports cached state, which can be a previous
          // attempt's failure that the fresh run then supersedes — rendering
          // it immediately flashes an error a moment before the data lands.
          // `complete` decides, once we know nothing better is coming.
          if (payload?.status === "FAILED" || payload?.errorMessage) {
            pendingError = String(
              payload?.errorMessage || "Transformation failed",
            );
            return;
          }
          // Still in flight (LOADING, or a progress message with no payload).
          if (!parquetData || typeof parquetData !== "string") return;
          // Set before awaiting: `complete` can fire while the parse is still
          // in flight, and must not mistake that for "no data arrived".
          sawData = true;
          pendingError = null;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http"),
            );
            const rows = arrowToRows(parsed as FullParsedParquetData);
            const done: FetchEntry = { loading: false, error: null, rows };
            detailCache.set(cacheKey, done);
            // Loaded: re-arm this cell so a later token expiry can recover.
            markSucceeded(cacheKey);
            if (!cancelled) setEntry(done);
          } catch (e: any) {
            const failed: FetchEntry = {
              loading: false,
              error: String(e?.message || e),
              rows: null,
            };
            detailCache.set(cacheKey, failed);
            if (!cancelled) setEntry(failed);
          }
        },
        error: (err: any) => {
          const message = String(err?.message || err);
          const fail = (reason: string) => {
            const failed: FetchEntry = {
              loading: false,
              error: reason,
              rows: null,
            };
            detailCache.set(cacheKey, failed);
            if (!cancelled) setEntry(failed);
          };
          // An auth rejection means the socket's token went stale. Recover
          // once per cell and resubscribe; anything else — or a rejection
          // after recovery — fails normally, so it can never loop.
          if (
            recoverFrom(cacheKey, message, {
              isCancelled: () => cancelled,
              onGiveUp: fail,
            })
          ) {
            detailCache.delete(cacheKey);
            return;
          }
          fail(message);
        },
        complete: () => {
          // The stream ended. If data arrived, the handler above already
          // rendered it. Otherwise surface the latched failure — or say the
          // request returned nothing, so a silent server-side failure can
          // never leave the popover spinning forever.
          if (sawData) return;
          const failed: FetchEntry = {
            loading: false,
            error: pendingError ?? "No data returned for this cell",
            rows: null,
          };
          detailCache.set(cacheKey, failed);
          if (!cancelled) setEntry(failed);
        },
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [
    target,
    runSettings,
    dateHierarchy,
    perspectiveApiService,
    retryNonce,
    recoverFrom,
    markSucceeded,
  ]);

  if (!target) return null;

  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={closePoCellPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            padding: "12px 14px",
            minWidth: 240,
            maxWidth: 320,
            border: "1px solid",
            borderColor: (t) => t.palette.atomic.border,
            borderRadius: "8px",
          },
        },
      }}
    >
      <PoCellPopoverContent target={target} entry={entry} />
    </Popover>
  );
}

function HeaderChip({ kind }: { kind: PoCellPopoverTarget["kind"] }) {
  const label =
    kind === "open"
      ? "OPEN PURCHASE ORDER"
      : kind === "simulated"
        ? "SIMULATED PO"
        : "PURCHASE ORDERS";
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        borderRadius: "4px",
        padding: "2px 7px",
        mb: "8px",
        backgroundColor: (t) =>
          kind === "simulated"
            ? t.palette.atomic.orange1
            : t.palette.atomic.blue1,
        color: (t) =>
          kind === "simulated"
            ? t.palette.atomic.ink
            : t.palette.atomic.brandBlueDark,
      }}
    >
      {label}
    </Box>
  );
}

function PoCellPopoverContent({
  target,
  entry,
}: {
  target: PoCellPopoverTarget;
  entry: FetchEntry;
}) {
  if (entry.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: "12px" }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (entry.error) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.red1 }}>
        Couldn't load PO details: {entry.error}
      </Box>
    );
  }
  const rows = entry.rows ?? [];
  if (rows.length === 0) {
    return (
      <Box>
        <HeaderChip kind={target.kind} />
        <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.muted }}>
          No PO details available for this cell.
        </Box>
      </Box>
    );
  }

  const totalCount = rows[0]?.total_po_count ?? rows.length;

  return (
    <Box>
      <HeaderChip kind={target.kind} />
      {totalCount > rows.length && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.muted,
            mb: "6px",
          }}
        >
          Showing top {rows.length} of {formatQty(totalCount)} POs by quantity
        </Box>
      )}
      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "2px" }}>
        {rows.map((row, i) => {
          // For aggregate ("all") cells each row picks its own layout from its
          // status: ERP rows use the open layout, engine rows the simulated one.
          const rowIsOpen =
            target.kind === "all"
              ? row.status !== "Needs Review"
              : target.kind === "open";
          return (
            <Box key={`${row.purchase_order_id}-${i}`}>
              {i > 0 && <Divider sx={{ my: "8px" }} />}
              {rowIsOpen ? (
                <>
                  <Box
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      color: (t) => t.palette.atomic.ink,
                      mb: "6px",
                    }}
                  >
                    {row.purchase_order_id}
                  </Box>
                  <FieldRow label="SKU" value={row.sku} />
                  <FieldRow label="Location" value={row.location} />
                  <FieldRow label="Quantity" value={formatQty(row.quantity)} />
                  <FieldRow
                    label="Arrival date"
                    value={formatDate(row.order_delivery_date)}
                  />
                  <FieldRow label="Status" value={row.status} />
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      backgroundColor: (t) => t.palette.atomic.orange1,
                      borderRadius: "6px",
                      padding: "8px 10px",
                      mb: "6px",
                    }}
                  >
                    <Box sx={FIELD_LABEL_SX}>Recommended arrival</Box>
                    <Box
                      sx={{
                        fontSize: "16px",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        color: (t) => t.palette.atomic.ink,
                      }}
                    >
                      {formatDate(row.order_delivery_date)}
                    </Box>
                  </Box>
                  <FieldRow label="SKU" value={row.sku} />
                  <FieldRow label="Location" value={row.location} />
                  <FieldRow
                    label="Suggested qty"
                    value={formatQty(row.quantity)}
                  />
                  <FieldRow
                    label="Place by"
                    value={formatDate(row.order_placed_date)}
                  />
                  <FieldRow
                    label="Lead time"
                    value={
                      row.lead_time_days != null
                        ? `${row.lead_time_days} days`
                        : "—"
                    }
                  />
                  {/*
                Only the wave the bin-packer ran over has a container. Omit the
                row entirely for later waves rather than rendering a dash — an
                unassigned future order has no container, which is different
                from one whose container is unknown.
              */}
                  {row.container_label != null && (
                    <FieldRow label="Container" value={row.container_label} />
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>
      <PopoverFooterLinks target={target} />
    </Box>
  );
}
